if (!customElements.get('price-range-slider')) {
  class PriceRangeSlider extends HTMLElement {
    constructor() {
      super();

      this.min = Number(this.dataset.min) || 0;
      this.max = Number(this.dataset.max) || 0;
      this.step = Math.max(1, Math.round((this.max - this.min) / 100) || 1);

      this.track = this.querySelector('.price-range-slider__track');
      this.rangeFill = this.querySelector('.price-range-slider__track-range');
      this.minHandle = this.querySelector('.price-range-slider__handle--min');
      this.maxHandle = this.querySelector('.price-range-slider__handle--max');
      this.minInput = this.querySelector('[data-price-range-min-input]');
      this.maxInput = this.querySelector('[data-price-range-max-input]');

      if (!this.track || !this.minHandle || !this.maxHandle || !this.minInput || !this.maxInput) return;

      this.minValue = this.clamp(this.parseInputValue(this.minInput), this.min, this.max);
      this.maxValue = this.clamp(this.parseInputValue(this.maxInput), this.min, this.max);

      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);

      this.minHandle.addEventListener('pointerdown', (event) => this.onHandlePointerDown(event, 'min'));
      this.maxHandle.addEventListener('pointerdown', (event) => this.onHandlePointerDown(event, 'max'));
      this.minHandle.addEventListener('keydown', (event) => this.onHandleKeyDown(event, 'min'));
      this.maxHandle.addEventListener('keydown', (event) => this.onHandleKeyDown(event, 'max'));

      this.minInput.addEventListener('input', () => this.onInputChange('min'));
      this.maxInput.addEventListener('input', () => this.onInputChange('max'));
      this.minInput.addEventListener('blur', () => this.onInputBlur('min'));
      this.maxInput.addEventListener('blur', () => this.onInputBlur('max'));

      this.render();
    }

    parseInputValue(input) {
      const value = parseInt(String(input.value).replace(/[^\d]/g, ''), 10);
      return Number.isNaN(value) ? 0 : value;
    }

    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }

    valueToPercent(value) {
      if (this.max === this.min) return 0;
      return ((value - this.min) / (this.max - this.min)) * 100;
    }

    percentToValue(percent) {
      const raw = this.min + (percent / 100) * (this.max - this.min);
      return Math.round(raw);
    }

    onHandlePointerDown(event, handleName) {
      event.preventDefault();
      this.activeHandle = handleName;
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (error) {
        // Pointer capture is best-effort; dragging still works via window listeners.
      }
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
    }

    onPointerMove(event) {
      if (!this.activeHandle) return;
      const rect = this.track.getBoundingClientRect();
      let percent = ((event.clientX - rect.left) / rect.width) * 100;
      percent = this.clamp(percent, 0, 100);
      this.setValue(this.activeHandle, this.percentToValue(percent));
    }

    onPointerUp() {
      this.activeHandle = null;
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
    }

    onHandleKeyDown(event, handleName) {
      const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
      if (!keys.includes(event.key)) return;
      event.preventDefault();

      const current = handleName === 'min' ? this.minValue : this.maxValue;
      let value = current;

      if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') value = current - this.step;
      if (event.key === 'ArrowRight' || event.key === 'ArrowUp') value = current + this.step;
      if (event.key === 'Home') value = this.min;
      if (event.key === 'End') value = this.max;

      this.setValue(handleName, value);
    }

    onInputChange(handleName) {
      const input = handleName === 'min' ? this.minInput : this.maxInput;
      const sanitized = input.value.replace(/[^\d]/g, '');
      if (sanitized !== input.value) input.value = sanitized;
      if (sanitized === '') return;

      this.setValue(handleName, parseInt(sanitized, 10), { skipInputUpdate: true });
    }

    onInputBlur(handleName) {
      const input = handleName === 'min' ? this.minInput : this.maxInput;
      const value = handleName === 'min' ? this.minValue : this.maxValue;
      input.value = value;
    }

    setValue(handleName, rawValue, { skipInputUpdate = false } = {}) {
      let value = this.clamp(rawValue, this.min, this.max);

      if (handleName === 'min') {
        value = Math.min(value, this.maxValue);
        this.minValue = value;
      } else {
        value = Math.max(value, this.minValue);
        this.maxValue = value;
      }

      this.render();

      if (!skipInputUpdate) {
        const input = handleName === 'min' ? this.minInput : this.maxInput;
        if (Number(input.value) !== value) {
          input.value = value;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    }

    render() {
      const minPercent = this.valueToPercent(this.minValue);
      const maxPercent = this.valueToPercent(this.maxValue);

      this.minHandle.style.left = `${minPercent}%`;
      this.maxHandle.style.left = `${maxPercent}%`;
      this.minHandle.setAttribute('aria-valuenow', this.minValue);
      this.maxHandle.setAttribute('aria-valuenow', this.maxValue);

      if (this.rangeFill) {
        this.rangeFill.style.left = `${minPercent}%`;
        this.rangeFill.style.right = `${100 - maxPercent}%`;
      }
    }
  }

  customElements.define('price-range-slider', PriceRangeSlider);
}
